import React, { useCallback, useEffect, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import { useTableState } from '../../../hooks/useTableState';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { getRendaVariavel, getSegmentos } from '../../../services/ativosService';
import { RendaVariavel, Segmento } from '../../../types/ativos';
import TableAtivos from '../components/TableAtivos';
import CadastroAtivoForm from '../../../components/CadastroAtivoForm';
import SnackbarAlert from '../../../components/SnackbarAlert';

const RendaVariavelPage: React.FC = () => {
  const [data, setData]           = useState<RendaVariavel[]>([]);
  const [segmentos, setSegmentos] = useState<Segmento[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [isCadastroOpen, setIsCadastroOpen] = useState(false);

  const { pagination, setPagination, sorting, setSorting, globalFilter, handleFilterChange } = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const fetchData = useCallback(async (
    pageIndex: number, pageSize: number, sortBy: typeof sorting, search: string,
  ) => {
    setLoading(true);
    try {
      const [res, segs] = await Promise.all([
        getRendaVariavel(pageIndex, pageSize, sortBy, search),
        getSegmentos(),
      ]);
      setData(res.results);
      setPageCount(res.totalPages);
      setSegmentos(segs);
    } catch {
      showError('Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter);
  }, [fetchData, pagination.pageIndex, pagination.pageSize, sorting, globalFilter]);

  const handleCadastroSuccess = () => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter);
    showSuccess('Cadastro realizado com sucesso!');
    setIsCadastroOpen(false);
  };

  const getSegmentoNome = useCallback((id: any): string =>
    segmentos.find(s => s.id === id)?.nome ?? 'Segmento desconhecido',
  [segmentos]);

  const columns = React.useMemo<ColumnDef<RendaVariavel>[]>(() => [
    { header: 'Nome',        accessorKey: 'nome' },
    { header: 'Código ISIN', accessorKey: 'codigo_isin' },
    { header: 'Segmento',    accessorFn: (row) => getSegmentoNome(row.segmento) },
  ], [getSegmentoNome]);

  return (
    <div>
      <div className="page-header">
        <h2>Renda Variável</h2>
        <IconButton onClick={() => setIsCadastroOpen(true)}><AddIcon /></IconButton>
      </div>
      <TableAtivos
        columns={columns} data={data} pageCount={pageCount}
        pagination={pagination} setPagination={setPagination}
        sorting={sorting} setSorting={setSorting}
        globalFilter={globalFilter} setGlobalFilter={handleFilterChange}
        isLoading={loading} segmentoNome="renda-variavel"
      />
      <Modal open={isCadastroOpen} onClose={() => setIsCadastroOpen(false)}>
        <div>
          <CadastroAtivoForm
            isOpen={isCadastroOpen}
            onRequestClose={() => setIsCadastroOpen(false)}
            onCadastroSuccess={handleCadastroSuccess}
            setSnackbarProps={() => {}}
          />
        </div>
      </Modal>
      <SnackbarAlert
        open={snackbar.open} message={snackbar.message}
        severity={snackbar.severity} onClose={closeSnackbar}
      />
    </div>
  );
};

export default RendaVariavelPage;
